const BASE_URL = process.env.REACT_APP_API_URL;

// ─── TOKEN MANAGEMENT ─────────────────────────────────────────────────────────
export const getToken        = () => localStorage.getItem('lavex_access_token');
export const getRefreshToken = () => localStorage.getItem('lavex_refresh_token');
export const setTokens = (access, refresh) => {
  localStorage.setItem('lavex_access_token', access);
  if (refresh) localStorage.setItem('lavex_refresh_token', refresh);
};
export const clearTokens = () => {
  localStorage.removeItem('lavex_access_token');
  localStorage.removeItem('lavex_refresh_token');
  localStorage.removeItem('lavex_user');
};

// ─── BASE REQUEST ─────────────────────────────────────────────────────────────
const request = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${endpoint}`, { ...options, headers });
  let data;
  try { data = await res.json(); } catch { data = {}; }

  if (res.status === 401 && getRefreshToken()) {
    try {
      const refreshRes = await fetch(`${BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: getRefreshToken() }),
      });
      const refreshData = await refreshRes.json();
      if (refreshData?.data?.accessToken) {
        setTokens(refreshData.data.accessToken, null);
        const retryRes = await fetch(`${BASE_URL}${endpoint}`, {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${refreshData.data.accessToken}` },
        });
        data = await retryRes.json();
        if (!retryRes.ok) throw new Error(data.message || 'Erreur serveur');
        return data;
      }
    } catch {
      clearTokens();
      window.location.href = '/login';
    }
  }

  if (!res.ok) throw new Error(data?.message || `Erreur ${res.status}`);
  return data;
};

const get  = (url, params) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request(`${url}${qs}`);
};
const post = (url, body) => request(url, { method: 'POST', body: JSON.stringify(body) });
const put  = (url, body) => request(url, { method: 'PUT',  body: JSON.stringify(body) });
const del  = (url)       => request(url, { method: 'DELETE' });

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login:          (body) => post('/auth/login', body),
  logout:         (body) => post('/auth/logout', body),
  refreshToken:   (body) => post('/auth/refresh-token', body),
  updatePassword: (body) => put('/auth/update-password', body),
};

// ─── ADMIN ────────────────────────────────────────────────────────────────────
export const adminApi = {
  // Dashboard
  getDashboard: () => get('/admin/dashboard'),

  // Validations entreprises (ancien système auth/register cat A)
  getPendingValidations: () => get('/admin/pending-validations'),
  validateUser:          (id, body) => put(`/admin/users/${id}/validate`, body),

  // Utilisateurs
  updateUser:         (id, body) => put(`/admin/users/${id}`, body),
  changeUserCategory: (id, body) => put(`/admin/users/${id}/category`, body),

  // Listes admin
  getLaveriesList: (params) => get('/admin/laveries', params),
  getLivreursList: (params) => get('/admin/livreurs', params),
  getClientsList:  (params) => get('/admin/clients',  params),

  // Laveries
  createLaverie:       (body)     => post('/admin/laveries', body),
  updateLaverieQuotas: (id, body) => put(`/admin/laveries/${id}/quotas`, body),
  deleteLaverie: (id, permanent = false) =>
    request(`/admin/laveries/${id}`, { method: 'DELETE', body: JSON.stringify({ permanent }) }),

  // Services (admin only)
  addService:    (lavId, body)      => post(`/admin/laveries/${lavId}/services`, body),
  updateService: (lavId, sid, body) => put(`/admin/laveries/${lavId}/services/${sid}`, body),
  deleteService: (lavId, sid)       => del(`/admin/laveries/${lavId}/services/${sid}`),

  // ── Demandes de services laveries (v2.2.2) ──────────────────────────────────
  getServiceRequests:    (params) => get('/admin/services/requests', params),
  approveServiceRequest: (id, body) => post(`/admin/services/requests/${id}/approve`, body),
  rejectServiceRequest:  (id, body) => post(`/admin/services/requests/${id}/reject`, body),

  // Pricing système
  getPricing: ()     => get('/admin/pricing'),
  setPricing: (body) => put('/admin/pricing', body),

  // Livreurs
  createLivreur:             (body)     => post('/admin/livreurs', body),
  updateLivreurQuotas:       (id, body) => put(`/admin/livreurs/${id}/quotas`, body),
  updateLivreurAvailability: (id, body) => put(`/admin/users/${id}`, body),
  deleteLivreur: (id, permanent = false) =>
    request(`/admin/livreurs/${id}`, { method: 'DELETE', body: JSON.stringify({ permanent }) }),

  // Stats
  getLaverieStats: (id) => get(`/admin/stats/laverie/${id}`),
  getLivreurStats: (id) => get(`/admin/stats/livreur/${id}`),
  getClientStats:  (id) => get(`/admin/stats/client/${id}`),

  // ── Impression commandes (v2.2) — données formatées pour impression/PDF ──────
  // Endpoint dédié retournant QR codes, barcode et données formatées
  getOrderPrintData: (orderId) => get(`/admin/laveries/orders/${orderId}/print`),

  // Discounts
  createDiscount:         (body)   => post('/admin/discounts', body),
  createLoyaltyDiscounts: (body)   => post('/admin/discounts/loyal-customers', body),
  getDiscountsList:       (params) => get('/admin/discounts', params),

  // ── SUPPORT (v2.2) ──────────────────────────────────────────────────────────
  // Admin: liste tous les tickets + stats + mettre à jour un ticket
  getSupportTickets: (params) => get('/admin/support/tickets', params),
  getSupportStats:   ()       => get('/admin/support/stats'),
  updateTicket:      (id, body) => put(`/admin/support/tickets/${id}`, body),
  // Messages d'un ticket (partagé user/admin via /support)
  getTicketMessages: (id)     => get(`/support/tickets/${id}`),
  sendTicketMessage: (id, body) => post(`/support/tickets/${id}/messages`, body),

  // ── DEMANDES ENTREPRISE (v2.2) ───────────────────────────────────────────────
  getEnterpriseRequests:  (params) => get('/admin/enterprise/requests', params),
  approveEnterpriseRequest: (id, body) => post(`/admin/enterprise/requests/${id}/approve`, body),
  rejectEnterpriseRequest:  (id, body) => post(`/admin/enterprise/requests/${id}/reject`, body),

  // ── COURSES v2.4 ─────────────────────────────────────────────────────────────
  // Courses livreurs en temps réel
  getActiveCourses: () => get('/admin/courses/active'),

  // ── LAVERIES v2.4 ────────────────────────────────────────────────────────────
  // Commandes picked_up pas encore reçues par la laverie
  getLaveriePendingReception: (id) => get(`/admin/laveries/${id}/pending-reception`),

  // ── RAPPORTS v2.4 ────────────────────────────────────────────────────────────
  getQRPerformanceReport: (params) => get('/admin/reports/qr-performance', params),
};

// ─── ORDERS ───────────────────────────────────────────────────────────────────
export const ordersApi = {
  list:   (params)   => get('/orders', params),
  get:    (id)       => get(`/orders/${id}`),
  cancel: (id, body) => put(`/orders/${id}/cancel`, body),
};

// ─── LAVERIES (public) ────────────────────────────────────────────────────────
export const laveriesApi = {
  list: (params) => get('/laveries', params),
  get:  (id)     => get(`/laveries/${id}`),
};

// ─── USERS ────────────────────────────────────────────────────────────────────
export const usersApi = {
  getProfile:           ()       => get('/users/profile'),
  updateProfile:        (body)   => put('/users/profile', body),
  getNotifications:     (params) => get('/users/notifications', params),
  markNotificationRead: (id)     => put(`/users/notifications/${id}/read`),
  markAllRead:          ()       => put('/users/notifications/read-all'),
  getPointsHistory:     (params) => get('/users/points-history', params),
};